import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency, COMMON_CURRENCIES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SUPPORTED_LANGUAGES } from "@/i18n";

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDaysIso = (baseIso, d) => {
  const dt = new Date(baseIso);
  dt.setDate(dt.getDate() + Number(d || 0));
  return dt.toISOString().slice(0, 10);
};

const emptyLine = () => ({
  product_id: null,
  name: "",
  description: "",
  unit: "",
  quantity: 1,
  unit_price: 0,
  tax_rate: 0,
  discount_percent: 0,
});

function lineTotals(li) {
  const qty = Number(li.quantity) || 0;
  const price = Number(li.unit_price) || 0;
  const disc = Number(li.discount_percent) || 0;
  const tax = Number(li.tax_rate) || 0;
  const base = qty * price;
  const discount = base * (disc / 100);
  const subtotal = base - discount;
  const taxAmount = subtotal * (tax / 100);
  return { subtotal, tax: taxAmount, total: subtotal + taxAmount };
}

export default function QuoteForm() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useAuth();
  const isEdit = !!id;
  const language = i18n.language?.split("-")[0] || "en";

  const [form, setForm] = useState({
    customer_id: "",
    currency: company?.default_currency || "SEK",
    issue_date: todayIso(),
    validity_days: 30,
    line_items: [emptyLine()],
    notes: "",
    footer: "",
    pdf_language: language,
  });
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get("/customers?is_active=true&page_size=100"),
          api.get("/products?is_active=true&page_size=100"),
        ]);
        setCustomers(c.data.items);
        setProducts(p.data.items);
      } catch (err) {
        toast.error(formatApiError(err, t("quotes.toasts.error")));
      }
    })();
  }, [t]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/quotes/${id}`);
        if (data.status !== "draft") {
          toast.error(t("quotes.toasts.cannotEditNonDraft"));
          navigate(`/app/quotes/${id}`, { replace: true });
          return;
        }
        setForm({
          customer_id: data.customer?.customer_id || "",
          currency: data.currency,
          issue_date: data.issue_date,
          validity_days: data.validity_days,
          line_items: data.line_items.map((li) => ({
            product_id: li.product_id ?? null,
            name: li.name,
            description: li.description || "",
            unit: li.unit || "",
            quantity: li.quantity,
            unit_price: li.unit_price,
            tax_rate: li.tax_rate,
            discount_percent: li.discount_percent,
          })),
          notes: data.notes || "",
          footer: data.footer || "",
          pdf_language: data.pdf_language,
        });
      } catch (err) {
        toast.error(formatApiError(err, t("quotes.toasts.error")));
        navigate("/app/quotes", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate, t]);

  const expiresAt = useMemo(
    () => addDaysIso(form.issue_date, form.validity_days),
    [form.issue_date, form.validity_days]
  );

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    for (const li of form.line_items) {
      const c = lineTotals(li);
      sub += c.subtotal;
      tax += c.tax;
    }
    return { subtotal: +sub.toFixed(2), tax: +tax.toFixed(2), total: +(sub + tax).toFixed(2) };
  }, [form.line_items]);

  const setLine = (idx, next) => {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((li, i) => (i === idx ? { ...li, ...next } : li)),
    }));
  };
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, emptyLine()] }));
  const rmLine = (idx) =>
    setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  const pickProduct = (idx, product_id) => {
    if (!product_id || product_id === "none") {
      setLine(idx, { product_id: null });
      return;
    }
    const p = products.find((x) => x.id === product_id);
    if (!p) return;
    setLine(idx, {
      product_id: p.id,
      name: p.name,
      description: p.description || "",
      unit: p.unit || "",
      unit_price: p.price,
      tax_rate: p.tax_rate,
    });
  };

  async function submit(e) {
    e.preventDefault();
    if (!form.customer_id) {
      toast.error(t("quotes.toasts.pickCustomer"));
      return;
    }
    if (form.line_items.length === 0 || form.line_items.some((li) => !li.name?.trim())) {
      toast.error(t("quotes.toasts.linesRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        currency: form.currency,
        issue_date: form.issue_date,
        validity_days: Number(form.validity_days) || 30,
        notes: form.notes || undefined,
        footer: form.footer || undefined,
        pdf_language: form.pdf_language,
        line_items: form.line_items.map((li) => ({
          product_id: li.product_id || undefined,
          name: li.name,
          description: li.description || undefined,
          unit: li.unit || undefined,
          quantity: Number(li.quantity),
          unit_price: Number(li.unit_price),
          tax_rate: Number(li.tax_rate),
          discount_percent: Number(li.discount_percent) || 0,
        })),
      };
      let res;
      if (isEdit) {
        res = await api.patch(`/quotes/${id}`, payload);
      } else {
        res = await api.post("/quotes", payload);
      }
      toast.success(isEdit ? t("quotes.toasts.updated") : t("quotes.toasts.created"));
      navigate(`/app/quotes/${res.data.id}`, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, t("quotes.toasts.error")));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className="p-6 text-slate-500" data-testid="quote-form-loading">{t("common.loading")}</div>;

  return (
    <div className="max-w-5xl space-y-6" data-testid="quote-form-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
        data-testid="quote-form-back"
      >
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </button>
      <h1 className="font-heading text-3xl tracking-tight text-slate-900">
        {isEdit ? t("quotes.editTitle") : t("quotes.newTitle")}
      </h1>

      <form onSubmit={submit} className="space-y-6" data-testid="quote-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>{t("quotes.customer")}</Label>
            <Select value={form.customer_id || "none"} onValueChange={(v) => setForm({ ...form, customer_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="quote-form-customer"><SelectValue placeholder={t("quotes.pickCustomer")} /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("quotes.currency")}</Label>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="quote-form-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COMMON_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("quotes.issueDate")}</Label>
            <Input
              type="date"
              value={form.issue_date}
              onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              data-testid="quote-form-issue-date"
            />
          </div>
          <div>
            <Label>{t("quotes.validityDays")}</Label>
            <Input
              type="number"
              min="1"
              max="365"
              value={form.validity_days}
              onChange={(e) => setForm({ ...form, validity_days: e.target.value })}
              data-testid="quote-form-validity"
            />
            <div className="mt-1 text-xs text-slate-500">
              {t("quotes.validUntil")}: <strong data-testid="quote-form-expires-preview">{expiresAt}</strong>
            </div>
          </div>
          <div>
            <Label>{t("quotes.pdfLanguage")}</Label>
            <Select value={form.pdf_language} onValueChange={(v) => setForm({ ...form, pdf_language: v })}>
              <SelectTrigger data-testid="quote-form-lang"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SUPPORTED_LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="font-semibold text-slate-800">{t("quotes.lineItems")}</div>
            <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="quote-form-add-line" className="gap-1">
              <Plus className="h-3.5 w-3.5" /> {t("quotes.addLine")}
            </Button>
          </div>
          <div className="space-y-3 p-4">
            {form.line_items.map((li, idx) => {
              const c = lineTotals(li);
              return (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start rounded-lg border border-slate-100 bg-slate-50/50 p-3" data-testid={`quote-line-${idx}`}>
                  <div className="md:col-span-3">
                    <Label className="text-xs">{t("quotes.product")}</Label>
                    <Select value={li.product_id || "none"} onValueChange={(v) => pickProduct(idx, v)}>
                      <SelectTrigger className="h-9"><SelectValue placeholder={t("quotes.custom")} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("quotes.custom")}</SelectItem>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-xs">{t("quotes.name")}</Label>
                    <Input value={li.name} onChange={(e) => setLine(idx, { name: e.target.value })} className="h-9" data-testid={`quote-line-name-${idx}`} />
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-xs">{t("quotes.qty")}</Label>
                    <Input type="number" step="any" value={li.quantity} onChange={(e) => setLine(idx, { quantity: e.target.value })} className="h-9" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">{t("quotes.unitPrice")}</Label>
                    <Input type="number" step="any" value={li.unit_price} onChange={(e) => setLine(idx, { unit_price: e.target.value })} className="h-9" />
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-xs">{t("quotes.taxPct")}</Label>
                    <Input type="number" step="any" value={li.tax_rate} onChange={(e) => setLine(idx, { tax_rate: e.target.value })} className="h-9" />
                  </div>
                  <div className="md:col-span-1 text-end">
                    <Label className="text-xs">{t("quotes.lineTotal")}</Label>
                    <div className="h-9 flex items-center justify-end font-medium tabular-nums text-sm">
                      {formatCurrency(c.total, form.currency, language)}
                    </div>
                  </div>
                  <div className="md:col-span-1 flex md:justify-end md:pt-6">
                    <Button type="button" size="sm" variant="ghost" onClick={() => rmLine(idx)} className="text-red-600 hover:text-red-700 h-9 w-9 p-0">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>{t("quotes.notes")}</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="quote-form-notes" />
          </div>
          <div>
            <Label>{t("quotes.footer")}</Label>
            <Textarea rows={3} value={form.footer} onChange={(e) => setForm({ ...form, footer: e.target.value })} data-testid="quote-form-footer" />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="space-y-0.5 text-sm">
            <div className="text-slate-500">{t("quotes.subtotal")}: <span className="tabular-nums">{formatCurrency(totals.subtotal, form.currency, language)}</span></div>
            <div className="text-slate-500">{t("quotes.tax")}: <span className="tabular-nums">{formatCurrency(totals.tax, form.currency, language)}</span></div>
            <div className="text-base font-semibold text-slate-900">{t("quotes.total")}: <span className="tabular-nums" data-testid="quote-form-grand-total">{formatCurrency(totals.total, form.currency, language)}</span></div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => navigate("/app/quotes")}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={submitting} data-testid="quote-form-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {isEdit ? t("quotes.save") : t("quotes.create")}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
